import { useState, MutableRefObject } from 'react';
import styled from 'styled-components';
import Head from 'next/head';
import { Space } from '@goatapp/web-content-components';
import { useTranslation } from 'react-i18next';
import { Code } from 'gift-cards/components/recipientLandingPage/Code';

import { formatGiftCardCode } from 'shared/utils/formatGiftCardCode';
import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { ProductGridSection } from 'homepage/components/sections/ProductGridSection';
import { requestService } from 'shared/utils/services/requestService';
import { transformSpaces } from 'gift-cards/utils/transformSpaces';
import { useIsBannerVisible } from 'shared/hooks/useIsBannerVisible';
import { useIsElementOnScreen } from 'gift-cards/hooks/useIsElementOnScreen';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { variableFontRegular } from 'styles/theme/text';

const customAnimation = `  
animation: fadeIn 4s, slide-in 2s forwards;

@keyframes slide-in {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(0%);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}`;

export const RecipientLandingPage = ({
  giftCardSpace,
  giftCardDetails,
  giftCardCodeDetails,
  giftCardGroup,
  isEmailEntryRequired = false,
}) => {
  const space = transformSpaces(giftCardSpace);
  const [isValidating, setIsValidating] = useState(false);
  const [giftCardCodeFromValidation, setGfitCardCodeFromValidation] = useState('');

  const { isMobile } = useWindowSize();
  const { t } = useTranslation();
  const isBannerVisible = useIsBannerVisible();

  const {
    containerRef: giftCardDetailsRef,
    isVisible: isGiftCardDetailsVisible,
  }: { containerRef: MutableRefObject<any>; isVisible: boolean } = useIsElementOnScreen({
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });

  const {
    containerRef: scrollViewRef,
    isVisible: isScrollViewVisible,
  }: { containerRef: MutableRefObject<any>; isVisible: boolean } = useIsElementOnScreen({
    root: null,
    rootMargin: '0px',
    threshold: 0,
  });

  const productGridSearchConfigs = {
    constructorResponse: undefined,
    sectionIndex: undefined,
    moduleTrackingData: {
      event_context: 'recipient_landing',
      draft_id: '18218',
      section_id: '104522',
      section_position: 8,
      section_type: 'ProductGridSection',
      section_name: 'For You',
      collection_id: giftCardGroup.collectionSlug,
      collection_slug: giftCardGroup.collectionSlug,
    },
    searchConfig: {
      type: 'collection',
      collectionId: giftCardGroup.collectionSlug,
      query: '',
      filterKey: '',
      filterValue: '',
      options: 'num_results_per_page=25',
    },
    title: 'For You',
  };

  // @todo: Integrate with BE endpoint when ready
  const handleSubmit = async (values, actions) => {
    setIsValidating(true);
    try {
      const response = await requestService({
        url: `/web-api/v1/gift-cards/view-code?giftCardId=${String(
          giftCardDetails.giftCardId,
        )}&recipientEmail=${String(values.email)}`,
        method: 'get',
      });
      setGfitCardCodeFromValidation(response?.giftCardCode);
      setIsValidating(false);
    } catch {
      actions.setFieldError('email', 'Invalid Email Address');
    } finally {
      setIsValidating(false);
    }
  };

  const code = formatGiftCardCode(giftCardCodeDetails?.giftCardCode || giftCardCodeFromValidation);

  return (
    <RecipientLandingPage.Wrapper>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <RecipientLandingPage.ScrollView ref={scrollViewRef}>
        <RecipientLandingPage.SpacePlusGiftCardDetails>
          <RecipientLandingPage.Space>
            <Space
              space={space}
              maxWidth={isMobile ? 254 : 294}
              animation={{ name: 'fadeIn', duration: 500 }}
            />
          </RecipientLandingPage.Space>

          <RecipientLandingPage.GiftCardDetails ref={giftCardDetailsRef}>
            <RecipientLandingPage.SenderAmount>
              <RecipientLandingPage.NameLabel>
                {t('giftCards.recipientLandingPage.to', 'To')}: {giftCardDetails?.recipientName}
              </RecipientLandingPage.NameLabel>
              {giftCardDetails?.senderName && (
                <RecipientLandingPage.NameLabel>
                  {t('giftCards.recipientLandingPage.from', 'From')}: {giftCardDetails?.senderName}
                </RecipientLandingPage.NameLabel>
              )}
              <div>
                {/* As of now giftcards are always in USD */}
                {formatMoneyObject({
                  currency: 'USD',
                  amount: giftCardDetails?.initialBalanceCents,
                })}
                &nbsp;
                {t('giftCards.recipientLandingPage.goatGiftCard', 'GOAT Gift Card')}
              </div>
            </RecipientLandingPage.SenderAmount>

            {giftCardDetails.message && (
              <RecipientLandingPage.Message>
                {giftCardDetails?.message}
              </RecipientLandingPage.Message>
            )}
            {!isMobile && (
              <Code
                code={code}
                isEmailEntryRequired={isEmailEntryRequired}
                isValidating={isValidating}
                handleSubmit={handleSubmit}
              />
            )}
          </RecipientLandingPage.GiftCardDetails>
        </RecipientLandingPage.SpacePlusGiftCardDetails>
      </RecipientLandingPage.ScrollView>

      <RecipientLandingPage.ProductGridSection>
        <ProductGridSection
          {...productGridSearchConfigs}
          isInViewPort={isScrollViewVisible}
          customAnimation={customAnimation}
          isFullScreenGrid
        />
        <RecipientLandingPage.FadeInCode>
          {!isMobile && !isGiftCardDetailsVisible && (
            <Code
              code={code}
              fadeIn
              isEmailEntryRequired={isEmailEntryRequired}
              isValidating={isValidating}
              handleSubmit={handleSubmit}
            />
          )}
        </RecipientLandingPage.FadeInCode>
      </RecipientLandingPage.ProductGridSection>

      {isMobile && (
        <RecipientLandingPage.MobileCode $isBannerVisible={isBannerVisible}>
          <Code
            code={code}
            isEmailEntryRequired={isEmailEntryRequired}
            isValidating={isValidating}
            handleSubmit={handleSubmit}
          />
        </RecipientLandingPage.MobileCode>
      )}
    </RecipientLandingPage.Wrapper>
  );
};

export default RecipientLandingPage;

RecipientLandingPage.Wrapper = styled.div`
  position: absolute;
  min-height: calc(100vh - 108px);
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow-x: scroll;
  ${hideScrollbars};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    position: initial;
    align-items: flex-start;
    overflow-y: hidden;
    min-height: 100%;
    margin-top: 16px;
  }
`;

RecipientLandingPage.ScrollView = styled.div`
  display: flex;
`;

RecipientLandingPage.SpacePlusGiftCardDetails = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-top: 22px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    max-width: 254px;
    margin-right: 64px;
    margin-top: 0;
  }
`;

RecipientLandingPage.Space = styled.div`
  display: flex;
  margin-right: 64px;
  margin-left: 128px;
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 0px 16px 8px;
  }
`;

RecipientLandingPage.GiftCardDetails = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  min-width: 304px;
  max-width: 304px;
  margin-right: 372px;
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    margin-right: 172px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 0 0 0 16px;
  }
`;

RecipientLandingPage.SenderAmount = styled.div`
  margin-bottom: 24px;
  ${variableFontRegular({ size: '13.5px', height: '17px' })};
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    ${variableFontRegular({ size: '11px', height: '14px' })};
    margin-bottom: 16px;
  }
`;

RecipientLandingPage.NameLabel = styled.div`
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
  margin-bottom: 8px;
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '9px', height: '9px' })};
    margin-bottom: 4px;
  }
`;

RecipientLandingPage.Message = styled.div`
  ${variableFontRegular({ size: '13.5px', height: '20px' })};
  margin-bottom: 32px;
  max-width: 304px;
  inline-size: 304px;
  overflow-wrap: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    ${variableFontRegular({ size: '11px', height: '14px' })};
    margin-bottom: 16px;
    max-width: 253px;
    inline-size: 253px;
  }
`;

RecipientLandingPage.ProductGridSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-top: 0px;
    > div {
      margin: 0;
    }
  }
`;

RecipientLandingPage.FadeInCode = styled.div`
  margin-top: 16px;
  height: 22px;
`;

RecipientLandingPage.MobileCode = styled.div<{ $isBannerVisible: boolean }>`
  width: 254px;
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-left: 16px;
    position: absolute;
    top: ${({ $isBannerVisible }) => (!$isBannerVisible ? '558px' : '598px')};
  }
`;
